import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Loader2, Trash2, Plus, Save, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from './format';

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '');

export const InsightsAdmin = ({ api, range }) => {
  const [doc, setDoc] = useState(null);
  const [docs, setDocs] = useState([]);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/insights');
    setDocs(data || []);
    const d = data?.[0] || null;
    setDoc(d);
    setItems(d?.items || []);
  }, [api]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/insights/generate', { start_date: range.startDate, end_date: range.endDate });
      setDoc(data); setItems(data.items);
      setDocs((list) => [data, ...list]);
      toast.success('Recomendações geradas como rascunho');
    } catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível gerar as recomendações'); }
    finally { setBusy(false); }
  };

  const save = async (status = doc.status) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/insights/${doc.id}`, { items, status });
      const justPublished = status === 'publicado' && doc.status !== 'publicado';
      setDoc(data); setItems(data.items);
      setDocs((list) => list.map((d) => (d.id === data.id ? data : d)));
      toast.success(justPublished ? 'Publicado no portal do cliente' : status !== doc.status ? 'Removido do portal do cliente' : 'Recomendações salvas');
    } catch (e) { toast.error('Não foi possível salvar'); }
    finally { setBusy(false); }
  };

  const pick = (id) => {
    const d = docs.find((x) => x.id === id);
    setDoc(d); setItems(d?.items || []);
  };

  const patch = (i, k, v) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, [k]: v } : it)));

  return (
    <Card data-testid="logistics-insights-admin">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-3 min-w-0 max-w-full">
            {docs.length > 1 && (
              <Select value={doc?.id} onValueChange={pick}>
                <SelectTrigger className="w-80 max-w-full" data-testid="insight-doc-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {docs.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {fmtDate(d.start_date)}–{fmtDate(d.end_date)} · {d.status === 'publicado' ? 'Publicado' : 'Rascunho'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {doc ? <span>Período {fmtDate(doc.start_date)} a {fmtDate(doc.end_date)} · gerado em {fmtDT(doc.created_at)} · {doc.status === 'publicado' ? 'publicado no portal' : 'rascunho'}</span> : 'Nenhuma recomendação gerada ainda.'}
          </div>
          <Button onClick={generate} disabled={busy} data-testid="logistics-insights-generate">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Gerar recomendações com IA
          </Button>
        </div>
        {doc && (
          <>
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={it.id || i} className="rounded-lg border p-3 space-y-2" data-testid={`insight-item-${i}`}>
                  <div className="flex gap-2">
                    <Input value={it.titulo} onChange={(e) => patch(i, 'titulo', e.target.value)} placeholder="Título" data-testid={`insight-title-${i}`} />
                    <Button variant="ghost" size="icon" onClick={() => setItems((a) => a.filter((_, j) => j !== i))} aria-label="Excluir" data-testid={`insight-delete-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea value={it.texto} onChange={(e) => patch(i, 'texto', e.target.value)} rows={3} data-testid={`insight-text-${i}`} />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <Button variant="outline" size="sm" onClick={() => setItems((a) => [...a, { titulo: '', texto: '' }])} data-testid="insight-add">
                <Plus className="h-4 w-4 mr-1" /> Adicionar recomendação
              </Button>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={doc.status === 'publicado'} disabled={busy} onCheckedChange={(on) => save(on ? 'publicado' : 'rascunho')} data-testid="insight-publish-switch" />
                  Publicar no portal do cliente
                </label>
                <Button size="sm" onClick={() => save()} disabled={busy} data-testid="insight-save">
                  <Save className="h-4 w-4 mr-1" /> Salvar alterações
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const InsightsPortal = ({ api }) => {
  const [doc, setDoc] = useState(undefined);
  useEffect(() => { api.get('/insights').then((r) => setDoc(r.data)).catch(() => setDoc(null)); }, [api]);
  if (!doc?.items?.length) return null;
  return (
    <div data-testid="logistics-insights-portal">
      <h2 className="text-lg font-semibold tracking-tight">Recomendações</h2>
      <p className="text-xs text-muted-foreground mb-3">
        Referente a {fmtDate(doc.start_date)} a {fmtDate(doc.end_date)} · publicado em {fmtDT(doc.published_at)}
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        {doc.items.map((it) => (
          <Card key={it.id}>
            <CardContent className="p-4 flex gap-3">
              <Lightbulb className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                {it.titulo && <p className="font-medium text-sm">{it.titulo}</p>}
                <p className="text-sm text-muted-foreground mt-0.5">{it.texto}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
